// Check this job on the screens: the same rules the connector's check_job runs (packages/engine/src/check.ts),
// over the job as the screens price it, every Build resolved live. Nothing is stored.

import { useMemo } from 'react';
import { checkJob, type CheckAddOn, type CheckBuild, type Finding, type MarketRange } from '@zerogap/engine';
import { useRows } from './api';
import { pricingOptions, usePricingRows } from './job';
import { useSession } from './session';
import type { JobCtx } from '../screens/job/Workspace';

/** The items a scope row became, by id (job_item_ids; several when one line is more than one box, 2026-10-18). */
export const scopeItemIds = (row: { job_item_ids?: unknown }): string[] => (Array.isArray(row.job_item_ids) ? (row.job_item_ids as unknown[]).map(String) : []);

export interface JobCheck {
  loading: boolean;
  findings: Finding[];
  /** How many scope rows the opportunity has: none means pass 1 cannot see what is missing. */
  scopeRows: number;
}

export function useJobCheck(ctx: JobCtx): JobCheck {
  const { data, r } = ctx;
  const s = useSession();
  const oppId = data.job?.opportunity_id as string | undefined;
  const scope = useRows('scope_row', { eq: { opportunity_id: oppId }, order: ['sort', 'created_at'], enabled: !!oppId });
  const linked = useRows('job_item', { in: { id: [...new Set(scope.rows.flatMap((x) => scopeItemIds(x)))] } });
  const marketRanges = s.setting<MarketRange[]>('market_ranges', []);
  const countertopRange = s.setting<{ family?: string | null; low: string | number | null; high: string | number | null } | null>('countertop_market_range', null);
  const compareInputs = s.setting<string[]>('check_compare_inputs', []);
  const skipInputs = s.setting<string[]>('check_skip_inputs', []);
  const job = data.job;
  const builds = data.builds;
  const addOns = data.addOns;
  // the check reads the items as the engine prices them: a Checked mark landing on a row does not re-run it (2026-10-24)
  const items = usePricingRows(data.items);

  return useMemo(() => {
    if (!r.res || !r.lib || !r.jobIn || !job || scope.loading || linked.loading) return { loading: true, findings: [], scopeRows: 0 };
    const rows = builds.length ? builds : [{ id: 'as-entered', name: 'the job as entered', is_base: true, global_overrides: {} }];
    const resolved: CheckBuild[] = [];
    for (const b of rows) {
      const res = r.resolveWith(pricingOptions(b, []));
      if (res) resolved.push({ id: b.id, name: String(b.name), is_base: !!b.is_base, resolution: res });
    }
    // each Add-on under each Build: an alternate's children waiting on a decision, and its own items' warnings (2026-10-17)
    const taken: CheckAddOn[] = [];
    for (const b of rows) {
      for (const a of addOns) {
        const res = r.resolveWith(pricingOptions(b, [a]));
        if (res) taken.push({ build_id: b.id, name: String(a.name), resolution: res });
      }
    }
    const elsewhere = new Set(linked.rows.filter((i) => i.job_id !== job.id).map((i) => i.id as string));
    const findings = checkJob({
      lib: r.lib,
      job: { linear_feet: job.linear_feet ?? null, build_type: job.build_type ?? null },
      builds: [...resolved.filter((b) => b.is_base), ...resolved.filter((b) => !b.is_base)],
      addOns: taken,
      scopeRows: scope.rows.map((x) => {
        const ids = scopeItemIds(x);
        return { id: x.id, request_text: String(x.request_text ?? ''), outcome: String(x.outcome ?? ''), job_item_ids: ids, item_elsewhere: ids.length > 0 && ids.every((id) => elsewhere.has(id)) };
      }),
      // every stored item on the job, an add-on's included: a scope row pointing at one is answered (2026-10-22)
      jobItemIds: items.map((i) => String(i.id)),
      subLines: r.jobIn.subLines,
      settings: { marketRanges, countertopRange: countertopRange ?? null, compareInputs, skipInputs },
    });
    return { loading: false, findings, scopeRows: scope.rows.length };
  }, [r, job, builds, addOns, items, scope.rows, scope.loading, linked.rows, linked.loading, marketRanges, countertopRange, compareInputs, skipInputs]);
}
